'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef } from 'react';
import { useApolloClient } from '@material-bank/graphql-client';
import { discoveryTileImageSource, preloadImages } from '@material-bank/mtag-utilities';
import { SIMILAR_PRODUCTS_QUERY, buildSimilarProductsVariables, type SimilarProductsResult } from '../queries/useSimilarProductsQuery';
import type { ProductByVariantSkusResult } from '../queries/useProductByVariantSkusQuery';
type UsePrefetchDiscoveryDataParams = {
  product: ProductByVariantSkusResult | null;
  activeVariant: string | null;
  locale: string;
  // Similar is for signed-in members only, so a guest's PDP warms nothing.
  enabled: boolean;
};

// The results a discovery screen shows first: the interstitial's ten orbit cards,
// which also cover the nine tiles the grid paints above the fold. Warming these,
// and no more, is what lets the orbit fill and the grid reveal with no pop-in —
// see useDiscoveryReveal, which reads the same session record.
const WARMED_RESULT_COUNT = 10;
// The interstitial opens on the product itself, on its first three
// orbit cards (INTERSTITIAL_SOURCE_CARD_COUNT in mtag-ui-components).
const SOURCE_TILE_COUNT = 3;
function warmAboveFoldTiles(products: {
  imageUrl: string;
}[] | undefined): void {
  if (!products?.length) {
    return;
  }

  // Low priority: this is speculative work for a screen the user may never open,
  // so it must not compete with the PDP's own images.
  void preloadImages(products.slice(0, WARMED_RESULT_COUNT).map(product => discoveryTileImageSource(product.imageUrl)), {
    priority: 'low'
  });
}
function warmSourceTiles(images: {
  url: string;
}[] | undefined): void {
  if (!images?.length) {
    return;
  }
  void preloadImages(images.slice(0, SOURCE_TILE_COUNT).map(image => discoveryTileImageSource(image.url)), {
    priority: 'low'
  });
}

// Warm the Apollo cache for the Similar results while the user is still on the
// PDP, so that screen opens from cache instead of firing cold. The page reads
// cache-first, so the prefetch only pays if it sends exactly the variables the
// page will, which is why both come from the same builder.
// Pairs With is left cold: nothing on the PDP links to it any more.
// Runs once per variable set (a ref guards re-runs; a colorway switch changes
// Similar's variationSku, so it warms again) and swallows failures — a prefetch
// must never surface an error.
export function usePrefetchDiscoveryData(t0) {
  const $ = _c(16);
  const {
    product,
    activeVariant,
    locale,
    enabled
  } = t0;
  const client = useApolloClient();
  const prefetchedKey = useRef(null);
  let t1;
  if ($[0] !== activeVariant || $[1] !== locale || $[2] !== product) {
    t1 = buildSimilarProductsVariables(product, activeVariant, locale);
    $[0] = activeVariant;
    $[1] = locale;
    $[2] = product;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const similarVariables = t1;
  const {
    sku
  } = similarVariables;
  let t2;
  if ($[4] !== client || $[5] !== enabled || $[6] !== product?.images || $[7] !== similarVariables || $[8] !== sku) {
    t2 = () => {
      if (!sku || !enabled) {
        return;
      }
      const key = JSON.stringify(similarVariables);
      if (prefetchedKey.current === key) {
        return;
      }
      prefetchedKey.current = key;
      warmSourceTiles(product?.images);
      client.query({
        query: SIMILAR_PRODUCTS_QUERY,
        variables: similarVariables
      }).then(_temp).catch(_temp2);
    };
    $[4] = client;
    $[5] = enabled;
    $[6] = product?.images;
    $[7] = similarVariables;
    $[8] = sku;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  let t3;
  if ($[10] !== client || $[11] !== enabled || $[12] !== product || $[13] !== similarVariables || $[14] !== sku) {
    t3 = [client, sku, similarVariables, product, enabled];
    $[10] = client;
    $[11] = enabled;
    $[12] = product;
    $[13] = similarVariables;
    $[14] = sku;
    $[15] = t3;
  } else {
    t3 = $[15];
  }
  useEffect(t2, t3);
}
function _temp2() {}
function _temp(t0) {
  const {
    data
  } = t0;
  return warmAboveFoldTiles(data?.similarProducts?.products);
}